import { useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Icon } from './Icon'
import styles from './ChipInput.module.css'

interface ChipInputProps {
  label: string
  hint?: string
  values: string[]
  onChange: (values: string[]) => void
  onInputChange?: (value: string) => void
  placeholder?: string
  required?: boolean
  warningFor?: (value: string) => string | undefined
}

export function ChipInput({ label, hint, values, onChange, onInputChange, placeholder, required, warningFor }: ChipInputProps) {
  const [input, setInput] = useState('')

  function add() {
    const v = input.trim()
    if (!v || values.includes(v)) return
    onChange([...values, v])
    setInput('')
    onInputChange?.('')
  }

  function remove(v: string) {
    onChange(values.filter(x => x !== v))
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); add() }
  }

  return (
    <div className={styles.field}>
      <label className={styles.label}>
        {label}
        {required && <span className={styles.req}>*</span>}
      </label>
      <div className={styles.inputWrap}>
        <input
          type="text"
          value={input}
          onChange={e => { setInput(e.target.value); onInputChange?.(e.target.value) }}
          onKeyDown={onKeyDown}
          onBlur={add}
          placeholder={placeholder}
        />
        <button type="button" className={styles.addBtn} onClick={add}>
          Add
        </button>
      </div>
      {hint && <div className={styles.hint}>{hint}</div>}
      {values.length > 0 && (
        <div className={styles.chips}>
          {values.map(v => {
            const warning = warningFor?.(v)
            return (
              <div key={v} className={styles.chipWrap}>
                <div className={`${styles.chip} ${warning ? styles.chipWarning : ''}`}>
                  {v}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => remove(v)}
                    aria-label={`Remove ${v}`}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </div>
                {warning && <div className={styles.chipWarningText}>{warning}</div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
